import React, { useEffect, useRef } from 'react';

// Slowly rotating dot-sphere rendered on canvas. Purely decorative.
export const ParticleField = ({ count = 900, accent = '#B6FF3C', alt = '#FF3B3B', altRatio = 0.12 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let frame;
    let width = 0;
    let height = 0;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const points = Array.from({ length: count }, () => {
      const theta = Math.acos(2 * Math.random() - 1);
      const phi = Math.random() * Math.PI * 2;
      return {
        theta,
        phi,
        color: Math.random() < altRatio ? alt : accent,
        size: Math.random() * 0.9 + 0.35,
      };
    });

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    let rotation = 0;
    const render = () => {
      rotation += 0.0012;
      ctx.clearRect(0, 0, width, height);
      const radius = Math.min(width, height) * 0.62;
      const cx = width / 2;
      const cy = height / 2;

      points.forEach((p) => {
        const phi = p.phi + rotation;
        const x = Math.sin(p.theta) * Math.cos(phi);
        const y = Math.cos(p.theta);
        const z = Math.sin(p.theta) * Math.sin(phi);
        const depth = (z + 1) / 2;
        const px = cx + x * radius;
        const py = cy + y * radius * 0.9;
        ctx.globalAlpha = 0.08 + depth * 0.55;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(px, py, p.size * (0.4 + depth * 0.8), 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      frame = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, [count, accent, alt, altRatio]);

  return <canvas ref={canvasRef} className="particle-canvas" data-testid="particle-field" aria-hidden="true" />;
};
